import React, { Component } from 'react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { observable, action, makeObservable } from 'mobx'
import dynamic from 'next/dynamic'

import { InfoTooltip } from '@elo-kit/components/info-tooltip/InfoTooltip'
import { ToggleField } from '@elo-kit/components/form/toggle-field/ToggleField'
import { useI18n } from '@elo-kit/components/i18n/i18n'
import { cleanupHTMLStyleFonts } from 'utils/fonts.utils'
import { TEXT_EDITOR_TYPE } from 'constants/forms.constants'

const EditorComponent = dynamic(() => import('@elo-kit/components/form/froala/EditorComponent'), {
  ssr: false,
})

const propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  fontFamilyList: PropTypes.shape({}),
  pluginsArrayDisable: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.element, PropTypes.string])),
}

const defaultProps = {
  disabled: false,
  fontFamilyList: {},
  pluginsArrayDisable: [],
}

@observer
class TextEditorFieldContainer extends Component {
  @observable textIsInvalid = false
  @observable showEditor = false

  componentDidMount() {
    const { value, withSwitcher } = this.props
    if (value || !withSwitcher) {
      this.toggleShowEditor(true)
    }
  }

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidUpdate() {
    const { isInvalid, validateOnUpdate } = this.props
    if (validateOnUpdate && this.textIsInvalid !== isInvalid) {
      this.setValidity(isInvalid)
    }
  }

  @action setValidity = (value) => {
    this.textIsInvalid = value
  }

  @action toggleShowEditor = (value) => {
    this.showEditor = value || !this.showEditor
  }

  render() {
    const {
      value,
      name,
      className,
      label,
      isInvalid,
      errorMessage,
      onChange,
      tooltipId,
      disabled,
      required,
      fontFamilyList,
      withSwitcher,
      I18n,
      ...props
    } = this.props

    const fieldClassNames = classNames(
      'field text-editor-field',
      {
        'field--disabled': disabled,
        'field--required': required,
        'text-editor-field--error': this.textIsInvalid,
      },
      className
    )

    const labelClassName = classNames('field__label', { 'field__label--no-margin': withSwitcher && !this.showEditor })

    const tooltipTitles = {
      invoice_setup_additional_info: I18n.t('react.cabinet.help_icon.invoice_setup_additional_info.content'),
      default_page_settings_description: I18n.t('react.cabinet.help_icon.default_page_settings_description.content'),
      cancellation_term_checkout_text: I18n.t('react.cabinet.help_icon.cancellation_term_checkout_text.content'),
      cancellation_term_description: I18n.t('react.cabinet.help_icon.cancellation_term_description.content'),
      mobile_app_in_app_view_content: I18n.t('react.cabinet.help_icon.mobile_app_in_app_view_content.content'),
    }

    return (
      <div className={fieldClassNames} onBlur={() => this.setValidity(isInvalid)}>
        {label && (
          <label className={labelClassName} htmlFor={name}>
            {withSwitcher && (
              <ToggleField
                id={`${tooltipId}_labelsEnabled`}
                handleChange={() => {
                  if (this.showEditor) {
                    onChange('')
                  }
                  this.toggleShowEditor()
                }}
                checked={this.showEditor}
                className='elo-toggle--without-margins'
              />
            )}
            <span>{label}</span>
            {tooltipId && <InfoTooltip id={`${tooltipId}_popover`} body={tooltipTitles[tooltipId]} />}
          </label>
        )}

        {this.textIsInvalid && (
          <div className='field__error'>
            {errorMessage}
            <i className='fas fa-exclamation-circle' />
          </div>
        )}
        {this.showEditor && (
          <EditorComponent
            id={name || 'text-editor'}
            tag='textarea'
            model={props.type === TEXT_EDITOR_TYPE.rich ? cleanupHTMLStyleFonts(value) : value}
            onModelChange={onChange}
            fontFamilyList={fontFamilyList}
            {...props}
          />
        )}
      </div>
    )
  }
}

TextEditorFieldContainer.displayName = 'TextEditorField'
TextEditorFieldContainer.propTypes = propTypes
TextEditorFieldContainer.defaultProps = defaultProps

export const TextEditorField = (props) => {
  const I18n = useI18n()

  return <TextEditorFieldContainer I18n={I18n} {...props} />
}
